import React, { useEffect, useState } from "react";
import { api, resolvePhotoUrl } from "@/lib/api";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Minus, Plus, Trash2, Link2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pending", "preparing", "ready", "dispatched", "cancelled"];

const badgeColors = {
    pending: "bg-clay/20 text-primary",
    preparing: "bg-moss/20 text-moss",
    ready: "bg-primary/20 text-primary",
    dispatched: "bg-surface text-muted-foreground",
    cancelled: "bg-destructive/10 text-destructive",
};

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("pending");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [localItems, setLocalItems] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (selectedOrder) setLocalItems(selectedOrder.items.map((i) => ({ ...i })));
    }, [selectedOrder?.id]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/orders", { params: filter ? { status: filter } : {} });
            setOrders(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [filter]);

    const changeStatus = async (id, status) => {
        try {
            await api.post(`/orders/${id}/status`, { status });
            toast.success("Status updated");
            load();
        } catch {
            toast.error("Could not update");
        }
    };

    const updateItemQty = (itemId, delta) => {
        setLocalItems((prev) =>
            prev.map((i) => i.item_id === itemId ? { ...i, quantity: Math.max(1, i.quantity + delta) } : i)
        );
    };

    const updateItemPrice = (itemId, value) => {
        setLocalItems((prev) =>
            prev.map((i) => i.item_id === itemId ? { ...i, price: value } : i)
        );
    };

    const deleteItem = (itemId) => {
        setLocalItems((prev) => prev.filter((i) => i.item_id !== itemId));
    };

    const saveChanges = async () => {
        setSaving(true);
        try {
            const original = selectedOrder.items;
            const deletedIds = original
                .filter((o) => !localItems.find((l) => l.item_id === o.item_id))
                .map((o) => o.item_id);
            const changed = localItems.filter((l) => {
                const o = original.find((orig) => orig.item_id === l.item_id);
                return o && o.quantity !== l.quantity;
            });

            if (deletedIds.length === 0 && changed.length === 0) {
                setSelectedOrder(null);
                return;
            }

            let updated = selectedOrder;
            for (const id of deletedIds) {
                const { data } = await api.delete(`/orders/${selectedOrder.id}/items/${id}`);
                updated = data;
            }
            for (const item of changed) {
                const orig = selectedOrder.items.find((o) => o.item_id === item.item_id);
                const priceChanged = orig && Number(orig.price) !== Number(item.price);
                const { data } = await api.patch(`/orders/${selectedOrder.id}/items/${item.item_id}`, {
                    quantity: item.quantity,
                    ...(priceChanged ? { price: Number(item.price) } : {}),
                });
                updated = data;
            }
            setSelectedOrder(updated);
            setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
            toast.success("Order updated");
        } catch {
            toast.error("Could not save changes");
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = selectedOrder && (
        localItems.length !== selectedOrder.items.length ||
        localItems.some((l) => {
            const o = selectedOrder.items.find((orig) => orig.item_id === l.item_id);
            return o && (o.quantity !== l.quantity || Number(o.price) !== Number(l.price));
        })
    );

    return (
        <>
        <section data-testid="admin-orders-page">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl">Orders</h2>
                <div className="w-48">
                    <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
                        <SelectTrigger data-testid="order-filter-select" className="rounded-full">
                            <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => (
                                <SelectItem key={s} value={s}>
                                    {s}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : orders.length === 0 ? (
                <div
                    data-testid="admin-orders-empty"
                    className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl"
                >
                    No orders yet.
                </div>
            ) : (
                <ul className="space-y-3">
                    {orders.map((o) => (
                        <li
                            key={o.id}
                            data-testid={`order-row-${o.id}`}
                            className="bg-card border border-border/60 rounded-2xl p-5"
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <p className="font-serif text-lg text-foreground cursor-pointer hover:underline" onClick={() => setSelectedOrder(o)}>
                                        {o.order_number}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {o.customer_name} · {o.customer_phone}
                                    </p>
                                    {o.customer_address && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {o.customer_address}
                                        </p>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {new Date(o.created_at).toLocaleString()}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span
                                        className={`text-xs uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
                                            badgeColors[o.status] || "bg-surface text-muted-foreground"
                                        }`}
                                    >
                                        {o.status}
                                    </span>
                                    <span className="font-serif text-2xl text-primary font-semibold">
                                        ₹{o.total.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                            <ul className="mt-4 space-y-1 text-sm">
                                {o.items.map((it, i) => (
                                    <li key={i} className="flex justify-between text-muted-foreground">
                                        <span>
                                            {it.quantity} × {it.name}
                                        </span>
                                        <span>₹{(it.price * it.quantity).toFixed(2)}</span>
                                    </li>
                                ))}
                            </ul>
                            {o.promo_code && (
                                <div
                                    data-testid={`order-promo-${o.id}`}
                                    className="mt-3 pt-3 border-t border-border/40 space-y-1 text-sm"
                                >
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>Subtotal</span>
                                        <span>₹{Number(o.subtotal || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full bg-moss/15 text-moss">
                                            <span>🎟</span>
                                            <span data-testid={`order-promo-code-${o.id}`}>{o.promo_code}</span>
                                            {o.promo_percent != null && (
                                                <span>· {Number(o.promo_percent).toFixed(0)}%</span>
                                            )}
                                        </span>
                                        <span
                                            data-testid={`order-promo-discount-${o.id}`}
                                            className="text-moss font-medium"
                                        >
                                            -₹{Number(o.discount_amount || 0).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {o.notes && (
                                <p className="mt-3 text-xs text-muted-foreground italic">
                                    Notes: {o.notes}
                                </p>
                            )}
                            <div className="mt-4 flex items-center gap-2">
                                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                    Update
                                </span>
                                <Select
                                    value={o.status}
                                    onValueChange={(v) => changeStatus(o.id, v)}
                                >
                                    <SelectTrigger
                                        data-testid={`order-status-select-${o.id}`}
                                        className="w-40 rounded-full h-9"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s}>
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>

        <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
            <DialogContent className="max-w-[95vw] w-full flex flex-col max-h-[90vh]">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-3 pr-6">
                        <DialogTitle className="font-serif text-xl">
                            {selectedOrder?.order_number}
                        </DialogTitle>
                        <button
                            onClick={(e) => {
                                const url = `${window.location.origin}/order/${selectedOrder.resource_id}`;
                                const container = e.currentTarget.closest('[role="dialog"]') || document.body;
                                const el = document.createElement("textarea");
                                el.value = url;
                                el.style.position = "fixed";
                                el.style.left = "0";
                                el.style.top = "0";
                                el.style.opacity = "0";
                                container.appendChild(el);
                                el.focus();
                                el.select();
                                const ok = document.execCommand("copy");
                                container.removeChild(el);
                                if (ok) {
                                    toast.success("Link copied!");
                                } else if (navigator.clipboard) {
                                    navigator.clipboard.writeText(url)
                                        .then(() => toast.success("Link copied!"))
                                        .catch(() => toast.error("Could not copy link"));
                                } else {
                                    toast.error("Could not copy link");
                                }
                            }}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground border border-border/60 rounded-full px-3 py-1.5 hover:border-foreground transition-colors flex-shrink-0"
                        >
                            <Link2 className="h-3.5 w-3.5" />
                            Copy link
                        </button>
                    </div>
                </DialogHeader>
                <div className="overflow-y-auto flex-1 space-y-3 pr-1">
                    {localItems.map((item) => (
                        <div key={item.item_id} className="flex items-center gap-4 py-2 border-b border-border/30 last:border-0">
                            <div className="h-16 w-16 rounded-xl bg-surface/70 border border-border/60 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                {item.photo_url ? (
                                    <img
                                        src={resolvePhotoUrl(item.photo_url)}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <span className="text-2xl">🛍</span>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{item.name}</p>
                                {item.category && (
                                    <p className="text-xs text-muted-foreground">{item.category}</p>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => updateItemQty(item.item_id, -1)}
                                    disabled={item.quantity <= 1}
                                    className="h-7 w-7 rounded-full border border-border/60 flex items-center justify-center hover:border-foreground disabled:opacity-30"
                                >
                                    <Minus className="h-3 w-3" />
                                </button>
                                <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                                <button
                                    onClick={() => updateItemQty(item.item_id, 1)}
                                    className="h-7 w-7 rounded-full border border-border/60 flex items-center justify-center hover:border-foreground"
                                >
                                    <Plus className="h-3 w-3" />
                                </button>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <span className="text-sm text-muted-foreground">₹</span>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.price}
                                    onChange={(e) => updateItemPrice(item.item_id, e.target.value)}
                                    className="w-20 text-sm font-medium text-right border border-border/60 rounded-lg px-2 py-1 bg-background focus:outline-none focus:border-foreground"
                                />
                            </div>
                            <p className="text-sm text-muted-foreground w-20 text-right flex-shrink-0">
                                = ₹{(Number(item.price) * item.quantity).toFixed(2)}
                            </p>
                            <button
                                onClick={() => deleteItem(item.item_id)}
                                className="h-7 w-7 rounded-full flex items-center justify-center text-destructive hover:bg-destructive/10 flex-shrink-0"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="pt-3 border-t border-border/40 space-y-1 text-sm">
                    {selectedOrder?.promo_code && (
                        <div className="flex justify-between text-muted-foreground">
                            <span>Subtotal</span>
                            <span>₹{localItems.reduce((s, i) => s + Number(i.price) * i.quantity, 0).toFixed(2)}</span>
                        </div>
                    )}
                    {selectedOrder?.promo_code && (
                        <div className="flex justify-between text-moss">
                            <span>Promo ({selectedOrder.promo_code})</span>
                            <span>-₹{(localItems.reduce((s, i) => s + Number(i.price) * i.quantity, 0) * (selectedOrder.promo_percent || 0) / 100).toFixed(2)}</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between font-semibold">
                        <span>Total</span>
                        <span className="font-serif text-primary">₹{(() => {
                            const sub = localItems.reduce((s, i) => s + Number(i.price) * i.quantity, 0);
                            const disc = sub * (selectedOrder?.promo_percent || 0) / 100;
                            return (sub - disc).toFixed(2);
                        })()}</span>
                    </div>
                    <div className="flex justify-end pt-2">
                        <button
                            onClick={saveChanges}
                            disabled={!hasChanges || saving}
                            className="px-5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-40"
                        >
                            {saving ? "Saving…" : "Update order"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
}
